import { useState, useEffect } from "react";
import { Scale, Play, Pause, RotateCcw, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface ScaleSimulationProps {
  onReading: (type: "tare" | "gross", weight: number, stable: boolean) => void;
  currentMode: "tare" | "gross" | "idle";
  laneId: number;
}

export default function ScaleSimulation({ onReading, currentMode, laneId }: ScaleSimulationProps) {
  const [currentWeight, setCurrentWeight] = useState(0);
  const [isStable, setIsStable] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [stabilityTimer, setStabilityTimer] = useState(0);

  useEffect(() => {
    if (!isRunning) return;

    const interval = setInterval(() => {
      // Simulate realistic weight fluctuations
      const baseWeight = currentMode === "tare" ? 15000 : 45000; // kg
      const fluctuation = (Math.random() - 0.5) * 100; // ±50kg fluctuation
      const newWeight = Math.max(0, baseWeight + fluctuation);
      
      setCurrentWeight(newWeight);

      // Simulate stability detection
      if (Math.abs(fluctuation) < 10) {
        setStabilityTimer(prev => {
          const newTimer = prev + 1;
          if (newTimer >= 3) { // Stable for 3 intervals
            setIsStable(true);
          }
          return newTimer;
        });
      } else {
        setStabilityTimer(0);
        setIsStable(false);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [isRunning, currentMode]);

  const handleCapture = () => {
    if (isStable && currentMode !== "idle") {
      onReading(currentMode, currentWeight, true);
      setIsRunning(false);
    }
  };

  const handleZero = () => {
    setCurrentWeight(0);
    setIsStable(false);
    setStabilityTimer(0);
  };

  const getStatusColor = () => {
    if (!isRunning) return "bg-gray-100 text-gray-800";
    if (isStable) return "bg-green-100 text-green-800";
    return "bg-yellow-100 text-yellow-800";
  };

  const getStatusText = () => {
    if (!isRunning) return "Stopped";
    if (isStable) return "Stable";
    return "Stabilizing";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <Scale className="w-5 h-5 mr-2" />
            Scale Lane {laneId}
          </div>
          <Badge className={getStatusColor()}>
            {getStatusText()}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Weight Display */}
          <div className="bg-black text-green-400 p-6 rounded-lg font-mono text-center">
            <div className="text-4xl font-bold">
              {currentWeight.toLocaleString()} kg
            </div>
            <div className="text-sm mt-2">
              Mode: {currentMode.toUpperCase()}
            </div>
          </div>

          {/* Stability Indicator */}
          <div className="flex items-center justify-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${isStable ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
            <span className="text-sm">
              {isStable ? "Weight Stable" : `Stabilizing... ${stabilityTimer}/3`}
            </span>
          </div>

          {/* Controls */}
          <div className="grid grid-cols-3 gap-2">
            <Button
              onClick={() => setIsRunning(!isRunning)}
              variant={isRunning ? "destructive" : "default"}
              size="sm"
              data-testid={`button-scale-${isRunning ? 'stop' : 'start'}`}
            >
              {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
            
            <Button
              onClick={handleZero}
              variant="outline"
              size="sm"
              data-testid="button-scale-zero"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
            
            <Button
              onClick={handleCapture}
              disabled={!isStable || currentMode === "idle"}
              variant="default"
              size="sm"
              className="bg-green-600 hover:bg-green-700"
              data-testid="button-capture-weight"
            >
              <TrendingUp className="w-4 h-4" />
            </Button>
          </div>

          <div className="text-xs text-gray-500 text-center">
            {currentMode === "idle" ? "Select weighing mode to begin" : 
             `Click capture when weight is stable for ${currentMode} reading`}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}